// src/i18n.js
// Kevyt i18n ilman i18nextiä (CRA-yhteensopiva, ei npm-riippuvuuksia).
//
// TÄRKEÄÄ:
// - Voit edelleen importata tämän kerran index.js:ssä (import "./i18n";), mutta se ei ole pakollista.
// - Käyttö: import { t, getLangFromPathname } from "./i18n";
//          const lang = getLangFromPathname(window.location.pathname);
//          t("intro.value", lang);

import fi from "./locales/fi.json";
import en from "./locales/en.json";
import es from "./locales/es.json";

export const supportedLngs = ["fi", "en", "es"];
export const fallbackLng = "fi";

export const resources = {
  fi,
  en,
  es,
};

let remoteResources = {};

export function setRemoteResources(nextResources = {}) {
  remoteResources = nextResources && typeof nextResources === "object"
    ? nextResources
    : {};
}

/**
 * Normalisoi kieli (palauta aina tuettu arvo)
 */
export function normalizeLang(lang) {
  return supportedLngs.includes(lang) ? lang : fallbackLng;
}

/**
 * Päättele kieli URL:sta:
 * - /      => fi
 * - /en/... => en
 * - /es/... => es
 */
export function getLangFromPathname(pathname = "/") {
  if (/^\/en(\/|$)/.test(pathname)) return "en";
  if (/^\/es(\/|$)/.test(pathname)) return "es";
  return "fi";
}

/**
 * Hae nested-avain esim "about.cards.0.title"
 */
function getByPath(obj, path) {
  if (!obj || !path) return undefined;
  const parts = String(path).split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur && Object.prototype.hasOwnProperty.call(cur, p)) cur = cur[p];
    else return undefined;
  }
  return cur;
}

/**
 * Kevyt templating: "Hello {{name}}" -> "Hello Arttu"
 */
function interpolate(str, vars) {
  if (!vars) return str;
  return String(str).replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_, key) => {
    const v = vars[key];
    return v === undefined || v === null ? "" : String(v);
  });
}

/**
 * t(key, lang?, vars?)
 * - fallback: jos avain puuttuu EN/ES:stä -> FI -> lopulta key
 */
export function t(key, lang = fallbackLng, vars = undefined) {
  const lng = normalizeLang(lang);

  const remotePrimary = getByPath(remoteResources[lng], key);
  if (remotePrimary !== undefined) return interpolate(remotePrimary, vars);

  const primary = getByPath(resources[lng], key);
  if (primary !== undefined) return interpolate(primary, vars);

  const remoteFallback = getByPath(remoteResources[fallbackLng], key);
  if (remoteFallback !== undefined) return interpolate(remoteFallback, vars);

  const fallback = getByPath(resources[fallbackLng], key);
  if (fallback !== undefined) return interpolate(fallback, vars);

  // viimeinen fallback: näytä avain, jotta puuttuva käännös huomataan
  return key;
}

/**
 * Apu: rakenna kieli-URL säilyttäen polku ilman kieliprefiksiä.
 * Esim nykyinen "/en/tietosuoja/" -> strip -> "/tietosuoja/"
 * -> lang "es" => "/es/tietosuoja/"
 */
export function buildLangPath(targetLang, pathname = "/") {
  const lng = normalizeLang(targetLang);

  // strip /en tai /es prefix
  let stripped = pathname.replace(/^\/(en|es)(?=\/|$)/, "");
  if (!stripped.startsWith("/")) stripped = `/${stripped}`;
  if (stripped === "") stripped = "/";

  if (lng === "fi") return stripped === "" ? "/" : stripped;
  return `/${lng}${stripped}`;
}

